'use client';

/**
 * AutoFillField Component
 *
 * Wrapper that decorates any input/display child with a tone-tinted
 * background + border + a status chip in the top-right. Generic — used
 * for any AI-pre-filled or human-required-input pattern across portals.
 *
 * The `children` is the actual input element (GlassTextField, MoneyField,
 * GlassSelect, MUI input, or a plain read-only display Box). Caller decides.
 *
 * i18n: caller passes the chip — do not hardcode chip labels in this primitive.
 */

import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { composeSx } from '../../utils/styledUtils';
import { GlassChip } from '../GlassChip';

// ============================================================================
// Types
// ============================================================================

export type AutoFillFieldState = 'autoFilled' | 'needsYou' | 'default';

export interface AutoFillFieldProps {
  /** Field label rendered above the wrapped input. */
  label?: ReactNode;
  /** State drives tone tint + default chip color. */
  state: AutoFillFieldState;
  /** Optional chip override. Pass i18n-translated label from the caller. */
  chip?: ReactNode;
  /** The actual input/display element. */
  children: ReactNode;
  /** Helper text rendered below the input. */
  helperText?: ReactNode;
  /** Marks the field as required (renders the MUI asterisk on the label). */
  required?: boolean;
  /** Visually puts the field in error state — overrides tone tint to error. */
  error?: boolean;
  /** id forwarded to the InputLabel `htmlFor`. */
  htmlFor?: string;
  /** Sx override on the outer wrapper. */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Tone resolver
// ============================================================================

function toneStyles(theme: Theme, state: AutoFillFieldState, error: boolean) {
  if (error) {
    return {
      bg: alpha(theme.palette.error.main, 0.08),
      border: alpha(theme.palette.error.main, 0.4),
    };
  }
  if (state === 'autoFilled') {
    return {
      bg: alpha(theme.palette.success.main, 0.08),
      border: alpha(theme.palette.success.main, 0.32),
    };
  }
  if (state === 'needsYou') {
    return {
      bg: alpha(theme.palette.warning.main, 0.08),
      border: alpha(theme.palette.warning.main, 0.4),
    };
  }
  return {
    bg: theme.palette.background.paper,
    border: theme.palette.divider,
  };
}

// ============================================================================
// Default chip (only shown when caller doesn't pass one)
// ============================================================================

function DefaultChip({ state }: { state: AutoFillFieldState }) {
  if (state === 'autoFilled') {
    return <GlassChip label="Astor" size="small" color="success" sx={{ height: 20, fontWeight: 600 }} />;
  }
  if (state === 'needsYou') {
    return <GlassChip label="Needs you" size="small" color="warning" sx={{ height: 20, fontWeight: 600 }} />;
  }
  return null;
}

// ============================================================================
// Main Component
// ============================================================================

export function AutoFillField({
  label,
  state,
  chip,
  children,
  helperText,
  required = false,
  error = false,
  htmlFor,
  sx,
}: AutoFillFieldProps) {
  const theme = useTheme();
  const tone = toneStyles(theme, state, error);
  const resolvedChip = chip ?? <DefaultChip state={state} />;

  return (
    <Box sx={composeSx({ position: 'relative', display: 'flex', flexDirection: 'column', gap: 0.75 }, sx)}>
      {label && (
        <InputLabel
          htmlFor={htmlFor}
          required={required}
          error={error}
          sx={{
            position: 'relative',
            transform: 'none',
            color: 'text.secondary',
            fontSize: '0.8125rem',
            fontWeight: 500,
            lineHeight: 1.35,
          }}
        >
          {label}
        </InputLabel>
      )}

      <Box
        sx={{
          position: 'relative',
          borderRadius: 1.5,
          border: `1px solid ${tone.border}`,
          backgroundColor: tone.bg,
          p: 1,
          transition: theme.transitions.create(['background-color', 'border-color'], { duration: 150 }),
        }}
      >
        {/* Chip is absolutely positioned so the input stays full-width */}
        {resolvedChip && (
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              right: 8,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            {resolvedChip}
          </Box>
        )}

        <Box
          sx={{
            // Reserve room on the right for the chip so children don't collide.
            pr: resolvedChip ? 8 : 0,
          }}
        >
          {children}
        </Box>
      </Box>

      {helperText && (
        <FormHelperText
          error={error}
          sx={{
            ml: 0,
            color: error ? 'error.main' : 'text.secondary',
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
}

AutoFillField.displayName = 'AutoFillField';

export default AutoFillField;
